import { configureStore } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';
import contractReducer from '../features/contracts/contractSlice';
import routesReducer from '../features/routes/routesSlice';
import sidebarReducer from '../layout/sidebar/sidebarSlice';
import distanceHeatingContractReducer from '../pages/distance-heating-contract/distanceHeatingContractSlice';
import authReducer from './slices/authSlice';
import userReducer from './slices/userSlice';

import * as Sentry from '@sentry/browser';
import localforage from 'localforage';
import { createTransform, persistReducer, persistStore } from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import authMiddleware from './middleware/authMiddleware';
import { sanitizeEvent } from './sentryReporting';

const ignoreErrors = [
  /ErrorResponse.*login_required/,
  /Error.*invalid_grant/,
  /Error.*Network Error/,
  /Error.*Frame window timed out/,
  /__firefox__/,
  /GTM-error.*Clarity not loaded/,
];

// Initiera Sentry
if (
  window?.location?.href.indexOf('localhost') === -1 &&
  window?.location?.href.indexOf('dev') === -1
) {
  Sentry.init({
    environment: window?.location?.hostname,
    dsn: 'https://23d1e0e22f1f4afeb65abfb0566211de@o1140508.ingest.sentry.io/6208811',
    release: `mitt-malarenergi@${import.meta.env.VITE_APP_ME_VERSION}`,
    beforeSend(event) {
      return sanitizeEvent(event, window.location.pathname);
    },
    ignoreErrors,
  });
}

// Skapa timestamp-transform för persist
const setTimestamp = createTransform(
  (inboundState) => {
    if (typeof inboundState === 'object' && inboundState !== null) {
      return { ...inboundState, _timestamp: Date.now() };
    }
    return inboundState;
  },
  (outboundState: any) => {
    const daysBeforePurge = 1;
    if (
      outboundState?._timestamp &&
      Date.now() - outboundState?._timestamp >
        daysBeforePurge * 24 * 60 * 60 * 1000
    ) {
      return undefined;
    }

    return outboundState;
  },
  {
    whitelist: [
      'user',
      'contracts',
      'forecasts',
      'facilities',
      'fjvContract',
      'fjvSimulation',
      'reports',
    ],
  },
);

export const persistConfig = {
  key: `me_${import.meta.env.VITE_APP_ME_ENV}`,
  storage: localforage,
  stateReconciler: autoMergeLevel2,
  blacklist: ['router', 'drainMeterReadings'],
  transforms: [setTimestamp],
};

const uiReducer = combineReducers({
  sidebar: sidebarReducer,
});

const persistedReducer = persistReducer(
  persistConfig,
  combineReducers({
    auth: authReducer,
    ui: uiReducer,
    user: userReducer,
    routes: routesReducer,
    contracts: contractReducer,
    fjvContract: distanceHeatingContractReducer,
  }) as any,
);

const rootReducer = (state: any, action: any) => {
  if (action.type === 'CLEAR_STORE') {
    state = undefined;
  }
  return persistedReducer(state, action);
};

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware: any) =>
    getDefaultMiddleware({
      thunk: {
        extraArgument: null,
      },
      serializableCheck: false,
      immutableCheck: false,
    }).concat([authMiddleware]),
});

export const persistor = persistStore(store);

export default store;
